import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
// Em produção o CSS vai inline no <head> (elimina a request bloqueante de render);
// em dev seguimos com <link> para preservar o HMR do Tailwind.
import appCssInline from "../styles.css?inline";
// Fontes latin-only (subsetting): Inter wght + Fraunces soft (normal + italic).
// Os arquivos do Fraunces são instâncias geradas a partir do @fontsource-variable
// com o eixo SOFT fixado em 30 — o mesmo valor que `.font-display` já aplicava via
// font-variation-settings. Fixar o eixo remove os deltas do SOFT sem alterar o
// desenho renderizado (61→36 KB normal, 76→46 KB itálico).
import frauncesSoftWoff2 from "../assets/fonts/fraunces-latin-soft30-normal.woff2?url";
import frauncesSoftItalicWoff2 from "../assets/fonts/fraunces-latin-soft30-italic.woff2?url";
import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";

const LATIN_RANGE =
  "U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD";

// As faces "* Fallback" apontam para fontes locais (Arial / Times New Roman e seus
// clones métricos) com size-adjust + ascent/descent-override calculados a partir
// dos arquivos reais. Enquanto a webfont carrega, o texto ocupa exatamente a mesma
// caixa — o swap deixa de reflowar o layout, que era a origem do CLS 0.16 no mobile
// (os orbs do hero, posicionados em % da altura da seção, deslizavam junto).
const FONT_FACE_CSS = `
@font-face{font-family:'Inter Variable';font-style:normal;font-display:swap;font-weight:100 900;src:url(${interLatinWoff2}) format('woff2-variations');unicode-range:${LATIN_RANGE};}
@font-face{font-family:'Fraunces Variable';font-style:normal;font-display:swap;font-weight:100 900;src:url(${frauncesSoftWoff2}) format('woff2-variations');unicode-range:${LATIN_RANGE};}
@font-face{font-family:'Fraunces Variable';font-style:italic;font-display:swap;font-weight:100 900;src:url(${frauncesSoftItalicWoff2}) format('woff2-variations');unicode-range:${LATIN_RANGE};}
@font-face{font-family:'Inter Fallback';font-style:normal;src:local('Arial'),local('Helvetica'),local('Liberation Sans');size-adjust:107.12%;ascent-override:90.44%;descent-override:22.52%;line-gap-override:0%;}
@font-face{font-family:'Fraunces Fallback';font-style:normal;src:local('Times New Roman'),local('Liberation Serif'),local('Tinos');size-adjust:127.14%;ascent-override:76.92%;descent-override:20.06%;line-gap-override:0%;}
@font-face{font-family:'Fraunces Fallback';font-style:italic;src:local('Times New Roman Italic'),local('Liberation Serif Italic'),local('Tinos Italic');size-adjust:125.66%;ascent-override:77.83%;descent-override:20.29%;line-gap-override:0%;}
`;

const RAW_GTM_ID = (import.meta.env.VITE_GTM_ID as string | undefined) ?? "";
const GTM_ID_VALID = /^GTM-[A-Z0-9]+$/i.test(RAW_GTM_ID) && RAW_GTM_ID !== "GTM-XXXXXXX";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Dr. Ulisses Contente — Psiquiatria por telemedicina" },
      {
        name: "description",
        content:
          "Atendimento psiquiátrico particular por telemedicina em todo o Brasil com o Dr. Ulisses Contente.",
      },
      { property: "og:title", content: "Dr. Ulisses Contente — Psiquiatria por telemedicina" },
      {
        property: "og:description",
        content:
          "Atendimento psiquiátrico particular por telemedicina em todo o Brasil com o Dr. Ulisses Contente.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Dr. Ulisses Contente — Psiquiatria por telemedicina" },
      { name: "twitter:description", content: "Atendimento psiquiátrico particular por telemedicina em todo o Brasil com o Dr. Ulisses Contente." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/4b214776-4dfb-4acf-8b16-a7ea00343bd6/id-preview-b4b31f19--64b81b8d-9137-4674-94a8-57d676c4661b.lovable.app-1783901578035.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/4b214776-4dfb-4acf-8b16-a7ea00343bd6/id-preview-b4b31f19--64b81b8d-9137-4674-94a8-57d676c4661b.lovable.app-1783901578035.png" },
    ],
    links: [
      // Os preloads das webfonts NÃO ficam aqui: `<HeadContent />` emite os
      // modulepreload do bundle antes desta lista, e eles disputariam banda com as
      // fontes do LCP. Eles são renderizados direto no <head> de RootShell, acima
      // de <HeadContent />, para garantir que sejam as primeiras requisições.
      //
      // Em produção o CSS é inlinado no <head> (ver RootShell) e não há request
      // bloqueante de render; em dev mantemos o <link> para o HMR do Tailwind.
      ...(import.meta.env.PROD ? [] : [{ rel: "stylesheet", href: appCss }]),
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      // dns-prefetch para o GTM (só útil quando o container estiver configurado)
      ...(GTM_ID_VALID
        ? ([{ rel: "dns-prefetch", href: "https://www.googletagmanager.com" }] as const)
        : []),
    ],
    
    scripts: GTM_ID_VALID
      ? [
          {
            children: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${RAW_GTM_ID}');`,
          },
        ]
      : [],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        {/* Primeiras requisições do documento: as 3 webfonts do texto acima da
            dobra. O H1 (LCP no mobile) mistura Fraunces normal e itálico, e o
            itálico antes só era descoberto depois do CSS + layout — daí os ~1,6 s
            de atraso de renderização do LCP. Precisam vir ACIMA de <HeadContent />,
            que emite os <link rel=modulepreload> do bundle JS. */}
        {[frauncesSoftWoff2, frauncesSoftItalicWoff2, interLatinWoff2].map((href) => (
          <link
            key={href}
            rel="preload"
            as="font"
            type="font/woff2"
            href={href}
            crossOrigin="anonymous"
            fetchPriority="high"
          />
        ))}
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: FONT_FACE_CSS }} />
        {/* Em produção o bundle de CSS (≈62 KB / ≈11 KB gzip) vai inline: some a
            request bloqueante de render de styles-*.css. Em dev o <link> emitido
            em `links` cuida do HMR. */}
        {import.meta.env.PROD ? (
          <style dangerouslySetInnerHTML={{ __html: appCssInline }} />
        ) : null}
      </head>
      <body>
        {GTM_ID_VALID ? (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${RAW_GTM_ID}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            />
          </noscript>
        ) : null}
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>
  );
}
